import React from 'react';
import { Box, Button, Flex, HStack, Icon, Text, Tooltip } from '@chakra-ui/react';
import { TbArchive, TbArchiveOff } from 'react-icons/tb';
import { Button as YaxleButton } from '../../ui';

const ACCENT = '#0071E3';
const ACCENT_HOVER = '#3544a0';

export interface InvoiceActionBarProps {
    /** When true, show Unarchive instead of Archive. */
    archived?: boolean;
    onSubmit: () => void;
    isSubmitting: boolean;
    disableSubmit?: boolean;
    submitDisabledHelpText?: string;
    /** When true, invoice is view-only (submitted or submitted with flags). */
    isReadOnly: boolean;
    onToggleArchive?: () => void;
    isArchiving?: boolean;
    /** Show Request Credit when submitted, negotiationEligible, and no open negotiation. */
    showRequestCredit?: boolean;
    /** Show View Negotiation when an open negotiation already exists. */
    showViewNegotiation?: boolean;
    onRequestCredit?: () => void;
    onViewNegotiation?: () => void;
    isStartingNegotiation?: boolean;
}

/** Sticky footer for the invoice detail panel: archive + negotiation + submit. */
const InvoiceActionBar = ({
    archived = false,
    onSubmit,
    isSubmitting,
    disableSubmit = false,
    submitDisabledHelpText,
    isReadOnly,
    onToggleArchive,
    isArchiving = false,
    showRequestCredit = false,
    showViewNegotiation = false,
    onRequestCredit,
    onViewNegotiation,
    isStartingNegotiation = false,
}: InvoiceActionBarProps) => {
    const submitIsDisabled = isReadOnly || disableSubmit;
    const showSubmitDisabledTooltip = Boolean(submitDisabledHelpText && disableSubmit && !isReadOnly);

    const submitButton = (
        <Button
            bg={ACCENT}
            color="white"
            size="sm"
            _hover={{ bg: ACCENT_HOVER }}
            _disabled={{ bg: 'gray.300', color: 'gray.500', cursor: 'not-allowed' }}
            onClick={onSubmit}
            isDisabled={submitIsDisabled}
            isLoading={isSubmitting}
            loadingText="Submitting..."
        >
            Submit
        </Button>
    );

    return (
        <Flex
            h="56px"
            flexShrink={0}
            borderTop="1px solid"
            borderColor="gray.200"
            bg="white"
            align="center"
            justify="flex-end"
            px={6}
        >
            <HStack spacing={2}>
                {onToggleArchive && (
                    <Button
                        size="sm"
                        variant="outline"
                        colorScheme={archived ? 'gray' : 'red'}
                        borderColor={archived ? undefined : 'red.300'}
                        color={archived ? undefined : 'red.600'}
                        bg={archived ? undefined : 'white'}
                        _hover={
                            archived
                                ? undefined
                                : { bg: 'red.50', borderColor: 'red.400', color: 'red.700' }
                        }
                        leftIcon={
                            <Icon
                                as={(archived ? TbArchiveOff : TbArchive) as React.ElementType}
                                boxSize={4}
                            />
                        }
                        onClick={onToggleArchive}
                        isLoading={isArchiving}
                        loadingText={archived ? 'Unarchiving…' : 'Archiving…'}
                    >
                        {archived ? 'Unarchive' : 'Archive'}
                    </Button>
                )}
                {showViewNegotiation && onViewNegotiation && (
                    <YaxleButton
                        size="sm"
                        variant="solid"
                        colorScheme="brand"
                        onClick={onViewNegotiation}
                    >
                        View Negotiation
                    </YaxleButton>
                )}
                {showRequestCredit && onRequestCredit && (
                    <YaxleButton
                        size="sm"
                        variant="solid"
                        colorScheme="brand"
                        onClick={onRequestCredit}
                        isLoading={isStartingNegotiation}
                        loadingText="Starting…"
                    >
                        Request Credit
                    </YaxleButton>
                )}
                {showSubmitDisabledTooltip ? (
                    <Tooltip
                        label={
                            <Text fontSize="xs" maxW="320px" whiteSpace="pre-line" textAlign="left">
                                {submitDisabledHelpText}
                            </Text>
                        }
                        placement="top-end"
                        hasArrow
                        openDelay={200}
                    >
                        <Box as="span" display="inline-block">
                            {submitButton}
                        </Box>
                    </Tooltip>
                ) : (
                    submitButton
                )}
            </HStack>
        </Flex>
    );
};

export default InvoiceActionBar;
